import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Navbar, Footer } from "../components/Layout";
import { toast, Toaster } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { trackFormSubmit } from "../lib/tracking";

export default function PublicForm() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/forms/${slug}`).then(r => {
      setForm(r.data);
      const init = {};
      r.data.fields.forEach(f => {
        if (f.type === "checkbox") init[f.name] = false;
        else if (f.type === "checkboxes") init[f.name] = [];
        else init[f.name] = f.default_value || "";
      });
      setValues(init);
      setLoading(false);
    }).catch(e => {
      setLoading(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Formulaire introuvable");
    });
  }, [slug]);

  const set = (name, v) => setValues(prev => ({ ...prev, [name]: v }));
  const toggleCheckbox = (name, opt) => setValues(prev => {
    const cur = new Set(prev[name] || []);
    if (cur.has(opt)) cur.delete(opt); else cur.add(opt);
    return { ...prev, [name]: Array.from(cur) };
  });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/forms/${slug}/submit`, values);
      trackFormSubmit(slug || "public_form", { form_slug: slug });
      if (data.redirect_url) { window.location.href = data.redirect_url; return; }
      setDone(data.message || "Merci !");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F9F9F7]"><Navbar /><div className="p-12 text-center font-mono">Chargement…</div></div>
  );
  if (!form) return (
    <div className="min-h-screen bg-[#F9F9F7]"><Navbar /><div className="max-w-2xl mx-auto p-12 text-center"><h1 className="font-display font-black text-2xl">Formulaire introuvable</h1></div><Footer /></div>
  );

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-2xl mx-auto px-6 md:px-8 py-16">
        {done ? (
          <div className="bg-white border-2 border-slate-900 p-10 text-center" data-testid="form-success">
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-600 mb-4"/>
            <h1 className="font-display font-black text-2xl md:text-3xl mb-3">{form.title}</h1>
            <p className="text-slate-700 whitespace-pre-wrap">{done}</p>
          </div>
        ) : (
          <>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-3" data-testid="public-form-title">{form.title}</h1>
            {form.description && <p className="text-slate-600 mb-8 whitespace-pre-wrap">{form.description}</p>}
            <form onSubmit={submit} className="bg-white border border-slate-900/15 p-6 md:p-8 space-y-5">
              {form.fields.map(fld => (
                <FieldRender
                  key={fld.name}
                  field={fld}
                  value={values[fld.name]}
                  onChange={v => set(fld.name, v)}
                  onToggle={(opt) => toggleCheckbox(fld.name, opt)}
                />
              ))}
              <button type="submit" disabled={submitting} data-testid="public-form-submit" className="btn-brutal btn-brutal-solar w-full px-6 py-3 font-bold uppercase tracking-wider text-sm">
                {submitting ? "Envoi…" : form.submit_label}
              </button>
            </form>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}

function FieldRender({ field, value, onChange, onToggle }) {
  const common = "input mt-1";
  const label = (
    <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
      {field.label} {field.required && <span className="text-red-600">*</span>}
    </span>
  );
  const help = field.help_text && <span className="text-xs text-slate-400 mt-1 block">{field.help_text}</span>;

  if (field.type === "textarea") {
    return <label className="block" data-testid={`field-${field.name}`}>{label}<textarea required={field.required} rows={5} placeholder={field.placeholder || ""} value={value || ""} onChange={e => onChange(e.target.value)} className={common}/>{help}</label>;
  }
  if (field.type === "select") {
    return <label className="block" data-testid={`field-${field.name}`}>{label}
      <select required={field.required} value={value || ""} onChange={e => onChange(e.target.value)} className={common}>
        <option value="">— Choisir —</option>
        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>{help}
    </label>;
  }
  if (field.type === "radio") {
    return <fieldset data-testid={`field-${field.name}`}>{label}
      <div className="mt-2 space-y-1">{(field.options || []).map(o => (
        <label key={o} className="flex items-center gap-2 text-sm">
          <input type="radio" name={field.name} value={o} checked={value === o} onChange={() => onChange(o)} required={field.required} className="accent-slate-900"/>{o}
        </label>
      ))}</div>{help}
    </fieldset>;
  }
  if (field.type === "checkbox") {
    return <label className="flex items-center gap-2 text-sm" data-testid={`field-${field.name}`}>
      <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} className="w-4 h-4 accent-slate-900"/>
      {field.label} {field.required && <span className="text-red-600">*</span>}
      {help}
    </label>;
  }
  if (field.type === "checkboxes") {
    const arr = value || [];
    return <fieldset data-testid={`field-${field.name}`}>{label}
      <div className="mt-2 space-y-1">{(field.options || []).map(o => (
        <label key={o} className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={arr.includes(o)} onChange={() => onToggle(o)} className="w-4 h-4 accent-slate-900"/>{o}
        </label>
      ))}</div>{help}
    </fieldset>;
  }
  // text, email, tel, number, date, url
  return <label className="block" data-testid={`field-${field.name}`}>{label}
    <input type={field.type} required={field.required} placeholder={field.placeholder || ""} value={value || ""} onChange={e => onChange(e.target.value)} className={common} minLength={field.min_length || undefined} maxLength={field.max_length || undefined}/>
    {help}
  </label>;
}
